import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, Columns3, Filter, Globe, LayoutList, Rows3, SearchX, Square, X,
} from 'lucide-react';
import { StartPage } from '../components/search/StartPage';
import { FacetRail } from '../components/search/FacetRail';
import { CompareTable, GroupedResults, ResultList } from '../components/search/ResultViews';
import { DetailPane } from '../components/search/DetailPane';
import { Dialog } from '../components/ui/Dialog';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Segmented } from '../components/ui/Segmented';
import { EmptyState, Notice, Spinner } from '../components/ui/misc';
import { useSources } from '../context/SourcesContext';
import { usePreferences } from '../context/PreferencesContext';
import { useSearchStream } from '../hooks/useSearchStream';
import { useIsDesktop, useIsWide } from '../hooks/useMediaQuery';
import { buildPackageModels, compareModels } from '../lib/resultModel';
import { activeFilterCount, applyFilters, applyRefine, computeFacetCounts, FACETS } from '../lib/facets';
import { patchParams, readSearchState } from '../lib/searchParams';
import { summariseLabs, isSettled } from '../lib/searchStatus';
import { buildHighlighter } from '../lib/highlight';
import { cx } from '../lib/cx';

const VIEW_OPTIONS = [
  { value: 'list', label: 'List', icon: LayoutList },
  { value: 'labs', label: 'By lab', icon: Rows3 },
  { value: 'compare', label: 'Compare', icon: Columns3 },
];

const SkeletonRows = () => (
  <div className="overflow-hidden rounded-xl border border-line bg-surface" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} className="border-b border-line px-4 py-3.5 last:border-b-0">
        <div className="h-3.5 w-2/5 animate-pulse rounded bg-raised" />
        <div className="mt-2 h-3 w-1/4 animate-pulse rounded bg-raised/70" />
        <div className="mt-2 h-3 w-3/5 animate-pulse rounded bg-raised/50" />
      </div>
    ))}
  </div>
);

const errorMessage = (error) => {
  if (!error) return '';
  if (error.status === 403) return error.message || 'Your role does not allow this search.';
  if (error.status === 400) return error.message;
  return error.message || 'The search failed.';
};

/**
 * Search results: filter rail (left), results (centre), details (right).
 * All state lives in the URL (see lib/searchParams.js).
 */
export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const sources = useSources();
  const prefs = usePreferences();
  const isDesktop = useIsDesktop();
  const isWide = useIsWide();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const headingId = useId();
  // History entry created by opening a result (so closing can simply go Back).
  const pushedKey = useRef(null);
  const justPushed = useRef(false);
  const lastOpenedId = useRef(null);

  const state = readSearchState(params);
  const nonce = location.state?.nonce || 0;
  // The compare table needs the full width, so details open as an overlay there.
  const docked = isWide && state.view !== 'compare';
  const mode = sources.effectiveMode(state.mode);
  const targets = useMemo(() => sources.searchTargets(mode), [sources, mode]);

  const canSearch = Boolean(state.q) && sources.status !== 'loading'
    && (targets.labCount > 0 || sources.status === 'error');
  const request = useMemo(() => (canSearch ? {
    query: state.q,
    mode,
    match: state.match,
    sources: targets.sources,
    customFolders: targets.customFolders,
    nonce,
  } : null), [canSearch, state.q, mode, state.match, targets, nonce]);

  const search = useSearchStream(request);
  const searching = search.status === 'searching';
  const finished = ['done', 'stopped', 'error'].includes(search.status);

  // Keep the page title meaningful for history and tabs.
  useEffect(() => {
    document.title = state.q ? `${state.q} – TCM Search` : 'TCM Search';
    return () => { document.title = 'TCM Search'; };
  }, [state.q]);

  // ── Models (built incrementally as packages arrive) ──────────────────────
  const cacheRef = useRef({ key: null, models: new WeakMap(), seen: new Map() });
  const models = useMemo(() => {
    const key = `${state.q}|${search.startedAt}|${sources.status}`;
    if (cacheRef.current.key !== key) cacheRef.current = { key, models: new WeakMap(), seen: new Map() };
    const cache = cacheRef.current;
    const all = [];
    search.packages.forEach((pkg) => {
      let pkgModels = cache.models.get(pkg);
      if (!pkgModels) {
        pkgModels = buildPackageModels(pkg, {
          sourcesByName: sources.sourcesByName,
          groupSizes: sources.groupSizes,
          query: state.q,
          seen: cache.seen,
        });
        cache.models.set(pkg, pkgModels);
      }
      all.push(...pkgModels);
    });
    return all.sort(compareModels);
  }, [search.packages, search.startedAt, state.q, sources.status, sources.sourcesByName, sources.groupSizes]);

  const filtersKey = JSON.stringify(state.filters);
  const refined = useMemo(() => applyRefine(models, state.refine), [models, state.refine]);
  const facetCounts = useMemo(
    () => computeFacetCounts(refined, state.filters),
    [refined, filtersKey], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const visible = useMemo(
    () => applyFilters(refined, state.filters),
    [refined, filtersKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const labSummary = useMemo(
    () => summariseLabs(search.packages, request ? targets.expected : {}, finished),
    [search.packages, request, targets.expected, finished],
  );
  const labTotal = Object.keys(labSummary).length;
  const labsSettled = Object.values(labSummary).filter((s) => isSettled(s.status)).length;
  const labsWithResults = Object.values(labSummary).filter((s) => s.results > 0).length;
  const truncatedLabs = Object.entries(labSummary).filter(([, s]) => s.truncated).map(([g]) => g);
  const failedLabs = Object.entries(labSummary).filter(([, s]) => s.status === 'error').map(([g]) => g);

  const highlighter = useMemo(
    () => (prefs.highlightTerms || prefs.highlightSamples
      ? buildHighlighter(prefs.highlightTerms ? (state.refine || state.q) : '', prefs.highlightSamples)
      : null),
    [prefs.highlightTerms, prefs.highlightSamples, state.q, state.refine],
  );

  const labHomepages = useMemo(
    () => Object.fromEntries(sources.labs.map((l) => [l.id, l.homepage])),
    [sources.labs],
  );

  // Announce completion to screen readers.
  useEffect(() => {
    if (search.status === 'done') {
      setAnnouncement(`${visible.length} results from ${labsWithResults} labs.`);
    }
  }, [search.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── URL updates ───────────────────────────────────────────────────────────
  const setUrl = useCallback((patch, { push = false } = {}) => {
    setParams((prev) => patchParams(prev, patch), { replace: !push, state: location.state });
  }, [setParams, location.state]);

  const toggleFilter = (key, value) => {
    const current = state.filters[key] || [];
    setUrl({ [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value] });
  };
  const clearFilters = () => setUrl({ lab: [], region: [], specimen: [], referral: [], refine: '' });

  const focusModel = useMemo(() => models.find((m) => m.id === state.focus) || null, [models, state.focus]);
  const focusIndex = focusModel ? visible.findIndex((m) => m.id === focusModel.id) : -1;

  const openResult = useCallback((id) => {
    lastOpenedId.current = id;
    if (!state.focus) {
      justPushed.current = true;
      setUrl({ focus: id }, { push: true });
    } else {
      setUrl({ focus: id });
    }
  }, [state.focus, setUrl]);

  useEffect(() => {
    if (state.focus && justPushed.current) {
      pushedKey.current = location.key;
      justPushed.current = false;
    }
    if (!state.focus) pushedKey.current = null;
  }, [state.focus, location.key]);

  const closeResult = useCallback(() => {
    // Going Back removes the entry we pushed, so the browser Back button and
    // the close button behave the same. Otherwise just drop the parameter.
    if (pushedKey.current && pushedKey.current === location.key) {
      navigate(-1);
    } else {
      setUrl({ focus: '' });
    }
    pushedKey.current = null;
    const id = lastOpenedId.current;
    window.requestAnimationFrame(() => {
      const row = id && document.querySelector(`[data-result-id="${id}"]`);
      if (row) row.focus({ preventScroll: false });
    });
  }, [location.key, navigate, setUrl]);

  const step = (delta) => {
    const next = visible[focusIndex + delta];
    if (next) openResult(next.id);
  };

  // Escape closes the docked pane (overlay variants handle Escape themselves).
  useEffect(() => {
    if (!focusModel || !docked) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      closeResult();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [focusModel, docked, closeResult]);

  const rerun = () => navigate(`${location.pathname}${location.search}`, { replace: true, state: { nonce: Date.now() } });

  if (!state.q) return <StartPage />;

  const filterCount = activeFilterCount(state.filters, state.refine);
  const resetKey = `${state.q}|${search.startedAt}|${filtersKey}|${state.refine}`;

  const facetRail = (
    <FacetRail
      facetCounts={facetCounts}
      filters={state.filters}
      onToggle={toggleFilter}
      onClearFacet={(key) => setUrl({ [key]: [] })}
      refine={state.refine}
      onRefineChange={(refine) => setUrl({ refine })}
      labSummary={labSummary}
      searching={searching}
    />
  );

  const detailPane = focusModel && (
    <DetailPane
      model={focusModel}
      position={focusIndex >= 0 ? focusIndex + 1 : 0}
      total={visible.length}
      onClose={closeResult}
      onPrev={focusIndex > 0 ? () => step(-1) : null}
      onNext={focusIndex >= 0 && focusIndex < visible.length - 1 ? () => step(1) : null}
      highlighter={highlighter}
      headingId={headingId}
    />
  );

  // ── Main column content ───────────────────────────────────────────────────
  let content;
  if (sources.status === 'loading') {
    content = <SkeletonRows />;
  } else if (!canSearch && targets.labCount === 0) {
    content = (
      <EmptyState icon={SearchX} title="No labs selected">
        Open <strong>Scope</strong> in the top bar and choose at least one lab to search.
      </EmptyState>
    );
  } else if (search.status === 'error' && !models.length) {
    content = (
      <Notice tone="danger" icon={AlertTriangle} title="The search didn't complete" action={<Button size="sm" onClick={rerun}>Try again</Button>}>
        {errorMessage(search.error)}
      </Notice>
    );
  } else if (!models.length && (searching || search.status === 'idle')) {
    content = <SkeletonRows />;
  } else if (!models.length) {
    content = (
      <EmptyState
        icon={SearchX}
        title={`No results for “${state.q}”`}
        actions={(
          <>
            {mode !== 'live' && state.match !== 'full' && (
              <Button variant="accent" onClick={() => setUrl({ match: 'full' })}>Search full text</Button>
            )}
            {mode !== 'live' && sources.canSearchLive && (
              <Button onClick={() => setUrl({ mode: 'live' })} icon={Globe}>Check live websites</Button>
            )}
          </>
        )}
      >
        Check the spelling, try a synonym or abbreviation, or search the full text of each entry.
        {search.status === 'stopped' && ' The search was stopped before every lab replied.'}
      </EmptyState>
    );
  } else if (!visible.length) {
    content = (
      <EmptyState icon={Filter} title="No results match your filters" actions={<Button onClick={clearFilters}>Clear filters</Button>}>
        {models.length.toLocaleString()} results are hidden by the current filters.
      </EmptyState>
    );
  } else if (state.view === 'labs') {
    content = (
      <GroupedResults
        models={visible}
        highlighter={highlighter}
        selectedId={state.focus}
        onOpen={openResult}
        labHomepages={labHomepages}
        resetKey={resetKey}
        followFocus={docked && Boolean(state.focus)}
      />
    );
  } else if (state.view === 'compare') {
    content = (
      <CompareTable models={visible} highlighter={highlighter} selectedId={state.focus} onOpen={openResult} resetKey={resetKey} />
    );
  } else {
    content = (
      <ResultList
        models={visible}
        highlighter={highlighter}
        selectedId={state.focus}
        onOpen={openResult}
        resetKey={resetKey}
        followFocus={docked && Boolean(state.focus)}
      />
    );
  }

  const chips = Object.entries(state.filters).flatMap(([key, values]) =>
    values.map((value) => ({ key, value, label: `${FACETS[key].label}: ${value.replace(/\s*-\s*Custom Databases$/, ' (custom)')}` })));

  return (
    <>
      <div className="mx-auto flex w-full max-w-[1600px] items-start gap-6 px-4 sm:px-6">
        {isDesktop && (
          <aside
            aria-label="Filters"
            className="sticky top-[var(--header-h,57px)] h-[calc(100dvh-var(--header-h,57px))] w-60 shrink-0 overflow-y-auto py-5 pr-1 scrollbar-thin xl:w-64"
          >
            {facetRail}
          </aside>
        )}

        <section aria-labelledby="results-heading" className="min-w-0 flex-1 py-4 sm:py-5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 id="results-heading" className="min-w-0 text-base font-semibold text-fg sm:text-lg">
              {!models.length && searching
                ? 'Searching…'
                : `${visible.length.toLocaleString()} result${visible.length === 1 ? '' : 's'}`}
              <span className="font-normal text-muted"> for “{state.q}”</span>
            </h1>
            {visible.length !== models.length && (
              <span className="text-sm text-muted">of {models.length.toLocaleString()}</span>
            )}
            <div className="ml-auto flex items-center gap-2">
              {!isDesktop && (
                <Button size="sm" icon={Filter} onClick={() => setFiltersOpen(true)} aria-label={`Filters${filterCount ? `, ${filterCount} active` : ''}`}>
                  Filters{filterCount > 0 && <Badge tone="accent" className="ml-0.5">{filterCount}</Badge>}
                </Button>
              )}
              <Segmented
                size="sm"
                label="Results view"
                value={state.view}
                // Compare needs the full width; close a docked result rather than covering the table.
                onChange={(view) => setUrl(view === 'compare' && docked ? { view, focus: '' } : { view })}
                options={VIEW_OPTIONS}
              />
            </div>
          </div>

          <div className="mt-1.5 flex min-h-[1.5rem] flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-muted">
            {mode === 'live' && <Badge tone="info" icon={Globe}>Live websites</Badge>}
            {mode !== 'live' && state.match === 'full' && <Badge tone="accent">Full text</Badge>}
            {searching && (
              <span className="inline-flex items-center gap-2">
                <Spinner size={13} />
                {labTotal ? `${labsSettled} of ${labTotal} labs replied` : 'Starting search'}
                <button type="button" onClick={search.stop} className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-fg-2 hover:bg-raised hover:text-fg">
                  <Square size={11} aria-hidden="true" /> Stop
                </button>
              </span>
            )}
            {search.status === 'done' && labTotal > 0 && (
              <span>
                {labsWithResults} of {labTotal} labs had matches
                {search.finishedAt && search.startedAt ? ` · ${((search.finishedAt - search.startedAt) / 1000).toFixed(1)}s` : ''}
              </span>
            )}
            {search.status === 'stopped' && <span>Stopped · {labTotal - labsSettled} labs not searched</span>}
          </div>

          {searching && labTotal > 0 && (
            <div className="mt-2 h-0.5 overflow-hidden rounded-full bg-line" aria-hidden="true">
              <div className="h-full bg-accent transition-[width] duration-300" style={{ width: `${Math.max(5, (labsSettled / labTotal) * 100)}%` }} />
            </div>
          )}

          {(chips.length > 0 || state.refine) && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5" aria-label="Active filters">
              {state.refine && (
                <button type="button" onClick={() => setUrl({ refine: '' })} className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2.5 py-1 text-xs text-accent-fg hover:bg-accent/20">
                  Contains: “{state.refine}” <X size={12} aria-label="remove" />
                </button>
              )}
              {chips.map((chip) => (
                <button
                  key={`${chip.key}:${chip.value}`}
                  type="button"
                  onClick={() => toggleFilter(chip.key, chip.value)}
                  className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2.5 py-1 text-xs text-accent-fg hover:bg-accent/20"
                >
                  {chip.label} <X size={12} aria-label="remove" />
                </button>
              ))}
              <button type="button" onClick={clearFilters} className="px-1.5 text-xs text-muted hover:text-fg">Clear all</button>
            </div>
          )}

          {(truncatedLabs.length > 0 || (failedLabs.length > 0 && models.length > 0) || (search.status === 'error' && models.length > 0)) && (
            <div className="mt-3 space-y-2">
              {truncatedLabs.length > 0 && (
                <Notice tone="warning" icon={AlertTriangle}>
                  {truncatedLabs.length === 1 ? truncatedLabs[0] : `${truncatedLabs.length} labs`} had more matches than can be shown,
                  so only the closest are listed. Make your search more specific to see the rest.
                </Notice>
              )}
              {failedLabs.length > 0 && models.length > 0 && (
                <p className="flex items-center gap-2 text-sm text-muted">
                  <AlertTriangle size={14} className="shrink-0 text-warning" aria-hidden="true" />
                  Couldn&apos;t reach {failedLabs.join(', ')}; showing results from the other labs.
                </p>
              )}
              {search.status === 'error' && models.length > 0 && (
                <Notice tone="danger" icon={AlertTriangle} action={<Button size="sm" onClick={rerun}>Try again</Button>}>
                  The connection dropped before every lab replied. {errorMessage(search.error)}
                </Notice>
              )}
            </div>
          )}

          <div className="mt-4">{content}</div>
          <p className="sr-only" aria-live="polite">{announcement}</p>
        </section>

        {focusModel && docked && (
          <aside
            aria-label="Result details"
            className="sticky top-[calc(var(--header-h,57px)+1rem)] my-4 h-[calc(100dvh-var(--header-h,57px)-2rem)] w-[min(34rem,38vw)] shrink-0 overflow-hidden rounded-xl border border-line shadow-sm"
          >
            {detailPane}
          </aside>
        )}
      </div>

      {focusModel && !docked && (
        <Dialog
          open
          onClose={closeResult}
          variant={isDesktop ? 'right' : 'full'}
          size={isDesktop ? 'xl' : 'full'}
          hideHeader
          labelledBy={headingId}
          bodyClassName="flex flex-col overflow-hidden"
          className={cx(isDesktop && 'max-w-[40rem]')}
        >
          {detailPane}
        </Dialog>
      )}

      {!isDesktop && (
        <Dialog
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          title="Filters"
          variant="bottom"
          size="full"
          footer={(
            <>
              <Button variant="ghost" onClick={clearFilters} disabled={!filterCount}>Clear all</Button>
              <Button variant="primary" onClick={() => setFiltersOpen(false)}>
                Show {visible.length.toLocaleString()} result{visible.length === 1 ? '' : 's'}
              </Button>
            </>
          )}
        >
          {facetRail}
        </Dialog>
      )}
    </>
  );
}
